import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchSelect, SearchSelectOptionProps } from "@alm/flow-ds/components";

import { ElementLayoutFieldProps } from "./types";
import { validateInputNumber } from "components/FormDesigner/helpers";
import {
	ELEMENTS_MIN_MAX_WIDTH,
	ELEMENT_PROPORTIONAL_DEFAULT_WIDTHS,
	PROPORTIONAL_MAX_WIDTH,
	PROPORTIONAL_MIN_WIDTH,
} from "./constants";
import { ElementLayout, ElementTypeList } from "schemas/elements";
import { propertyType, UpdateData } from "hooks/FormDesigner/useElementList";
import { Actions } from "@alm/flow-ds/dist/components/SearchSelect/SearchSelect";

const useGetMinMaxProportional = (elem: ElementTypeList) => {
	const { min, max } = ELEMENTS_MIN_MAX_WIDTH[elem] ?? {
		min: PROPORTIONAL_MIN_WIDTH,
		max: PROPORTIONAL_MAX_WIDTH,
	};

	return { min, max };
};

const useElementProportionalWidthOptions = () => {
	const [options, setOptions] = useState<SearchSelectOptionProps[]>([]);
	useEffect(() => {
		const newOptions: SearchSelectOptionProps[] = [];
		for (const width of ELEMENT_PROPORTIONAL_DEFAULT_WIDTHS) {
			newOptions.push({
				value: width.toString(),
				label: width.toString(),
			});
		}
		setOptions(newOptions);
	}, []);

	return { options };
};

interface UseProportionalWidth {
	value: ElementLayout;
	updateStore: (data: UpdateData) => void;
	elementIndex: number;
	field: propertyType;
	elem: ElementTypeList;
}

const useProportionalWidth = ({
	value,
	updateStore,
	elementIndex,
	field,
	elem,
}: UseProportionalWidth) => {
	const [disabled, setDisabled] = useState<boolean>(false);
	const [proportionalWidth, setProportionalWidth] = useState<number | null>(
		value?.proportionalWidth || null
	);
	const [inputValue, setInputValue] = useState<{
		value: string | number;
		label: string | number;
	} | null>(null);
	const { min, max } = useGetMinMaxProportional(elem);
	const { options } = useElementProportionalWidthOptions();

	useEffect(() => {
		setDisabled(min === max);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleOnBlur = () =>
		updateStore({
			elementIndex,
			property: field,
			value:
				proportionalWidth === null || isNaN(proportionalWidth)
					? { ...value, proportionalWidth: null }
					: { ...value, proportionalWidth },
		});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleOnChange = (value: any, action?: Actions) => {
		if (action?.action === "clear") {
			setProportionalWidth(null);
			return;
		}
		const validatedValue: number | null = validateInputNumber(
			+value.value,
			min,
			max
		);
		setProportionalWidth(validatedValue);
	};

	useEffect(() => {
		const displayValue = disabled
			? min
			: proportionalWidth?.toString() || "";
		if (displayValue === "") setInputValue(null);
		else
			setInputValue({
				value: displayValue,
				label: displayValue,
			});
	}, [disabled, min, proportionalWidth]);

	return {
		options,
		disabled,
		proportionalWidth,
		inputValue,
		handleOnBlur,
		handleOnChange,
	};
};

const ProportionalWidth = ({
	id,
	value,
	field,
	elementIndex,
	updateStore,
	elem,
}: ElementLayoutFieldProps) => {
	const { options, disabled, inputValue, handleOnBlur, handleOnChange } =
		useProportionalWidth({ value, updateStore, elementIndex, field, elem });

	const { t } = useTranslation();

	const handleOnCreate = useCallback(
		input => handleOnChange({ value: input }),
		[handleOnChange]
	);
	return (
		<SearchSelect
			id={`proportional-width-${id}`}
			onChange={handleOnChange}
			onBlur={handleOnBlur}
			label={t("formDesigner.labels.proportionalWidth")}
			hint={t("formDesigner.text.proportionalWidth")}
			className=""
			value={inputValue as SearchSelectOptionProps}
			options={options}
			debounceTimeout={400}
			clearable={true}
			onCreate={handleOnCreate}
			disabled={disabled}
			placeholder={t(`formDesigner.placeholders.select`)}
		/>
	);
};

export default React.memo(
	ProportionalWidth,
	(prevProps, nextProps) =>
		prevProps.value?.proportionalWidth ===
			nextProps.value?.proportionalWidth &&
		prevProps.value?.lineBreakAfterElement ===
			nextProps.value?.lineBreakAfterElement
);
